import React, {
  FC,
  Fragment,
  JSX,
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSyncedScroll } from '../hooks/useSyncedScroll';
import { createPortal } from 'react-dom';
import classNames from 'classnames/bind';
import { Loader } from '@entur/loader';
import { useIntl } from 'react-intl';
import { formatAmountCurrency } from 'utils/numberUtils';
import { Heading4 } from '@entur/typography';
import { DoubleScrollbar } from 'components/DoubleScrollbar';
import styles from '../styles.module.scss';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { useSuspenseMyCharts } from 'features/charts-of-accounts/api/getMyCharts';
import { useSuspenseSegment3s } from 'features/segment3/api/getSegment3s';
import { AccountingLineDisplay } from './types';
import { calculateTotals, FILTERED_COLUMNS, groupRowsByAccount, padAccountsToSameLength } from './utils';

const cx = classNames.bind(styles);

interface TAccountProps {
  rows?: object[];
  children?: ReactNode;
  stickyHeader?: boolean;
}

const AccountNameInfo = ({ chartId, accountCode }: { chartId: number; accountCode: string }): JSX.Element => {
  const {
    data: { data: segment3Data },
  } = useSuspenseSegment3s({
    chartOfAccountsId: chartId,
    searchParams: { code: accountCode, discontinued: false },
  });
  const account = segment3Data.items?.find((a) => a.code === accountCode);
  return <span>{account ? `${account.name} (${accountCode})` : accountCode}</span>;
};

export const TAccount = ({ rows = [], children, stickyHeader = false }: TAccountProps) => {
  const intl = useIntl();
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<HTMLDivElement>(null);
  const { syncScroll } = useSyncedScroll();
  const [scrollWidth, setScrollWidth] = useState(0);

  useEffect(() => {
    if (!stickyHeader || !contentRef.current) return;
    const update = () => {
      if (contentRef.current) setScrollWidth(contentRef.current.scrollWidth);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [stickyHeader]);

  const handleContentScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => syncScroll((e.target as HTMLDivElement).scrollLeft, [scrollbarRef]),
    [syncScroll]
  );
  const handleScrollbarScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => syncScroll((e.target as HTMLDivElement).scrollLeft, [contentRef]),
    [syncScroll]
  );

  const content = (
    <div className={cx({ taccountContainer: true })}>
      <ErrorBoundary>
        <Suspense fallback={<Loader>{intl.formatMessage({ id: 'generalValues.loading' })}</Loader>}>
          <TAccountBody
            rows={rows}
            stickyHeader={stickyHeader}
            scrollContainerRef={stickyHeader ? contentRef : undefined}
          />
        </Suspense>
      </ErrorBoundary>
    </div>
  );

  return (
    <>
      {children}
      {stickyHeader ? (
        <>
          <div ref={contentRef} className={styles.taccountScrollWrapper} onScroll={handleContentScroll}>
            {content}
          </div>
          <div ref={scrollbarRef} className={styles.stickyScrollbar} onScroll={handleScrollbarScroll}>
            <div style={{ width: scrollWidth, height: 1 }} />
          </div>
        </>
      ) : (
        <DoubleScrollbar>{content}</DoubleScrollbar>
      )}
    </>
  );
};

interface TAccountBodyProps extends TAccountProps {
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
}

const TAccountBody: FC<TAccountBodyProps> = ({ rows = [], stickyHeader = false, scrollContainerRef }) => {
  const intl = useIntl();
  const {
    data: { data: chartsOfAccounts },
  } = useSuspenseMyCharts();

  const { groupedData, accounts, accountTotals } = useMemo(() => {
    const { tempAccounts, groupedByAccount } = groupRowsByAccount(rows);
    padAccountsToSameLength(tempAccounts);
    const totals = calculateTotals(groupedByAccount);

    return {
      groupedData: groupedByAccount,
      accounts: tempAccounts,
      accountTotals: totals,
    };
  }, [rows]);

  // Ghost header state
  const [ghostVisible, setGhostVisible] = useState(false);
  const [activeOrgCode, setActiveOrgCode] = useState<string | null>(null);
  const [ghostLeft, setGhostLeft] = useState(0);
  const [ghostWidth, setGhostWidth] = useState(0);
  const [ghostColWidths, setGhostColWidths] = useState<number[]>([]);
  const [ghostTableWidth, setGhostTableWidth] = useState(0);
  const ghostScrollRef = useRef<HTMLDivElement>(null);
  const theadRefs = useRef<Map<string, HTMLTableSectionElement>>(new Map());
  const tableRefs = useRef<Map<string, HTMLTableElement>>(new Map());

  // Track scroll container geometry for fixed positioning
  useEffect(() => {
    if (!stickyHeader || !scrollContainerRef?.current) return;
    const update = () => {
      const el = scrollContainerRef.current!;
      const rect = el.getBoundingClientRect();
      setGhostLeft(rect.left);
      setGhostWidth(rect.width);
    };
    update();
    const obs = new ResizeObserver(update);
    obs.observe(scrollContainerRef.current);
    return () => obs.disconnect();
  }, [stickyHeader, scrollContainerRef]);

  // IntersectionObserver: find which org code's thead has scrolled above viewport
  useEffect(() => {
    if (!stickyHeader) return;
    const orgCodes = Object.keys(groupedData);

    const updateGhost = () => {
      let bestOrgCode: string | null = null;
      let bestBottom = -Infinity;

      orgCodes.forEach((orgCode) => {
        const thead = theadRefs.current.get(orgCode);
        const table = tableRefs.current.get(orgCode);
        if (!thead || !table) return;
        const headRect = thead.getBoundingClientRect();
        const tableRect = table.getBoundingClientRect();
        if (headRect.bottom < 0 && tableRect.bottom > 0 && headRect.bottom > bestBottom) {
          bestBottom = headRect.bottom;
          bestOrgCode = orgCode;
        }
      });

      if (bestOrgCode) {
        setActiveOrgCode(bestOrgCode);
        setGhostVisible(true);
        const table = tableRefs.current.get(bestOrgCode);
        if (table) {
          const firstBodyRow = table.querySelector('tbody tr');
          if (firstBodyRow) {
            const tds = Array.from(firstBodyRow.querySelectorAll('td'));
            setGhostColWidths(tds.map((td) => td.getBoundingClientRect().width));
          }
          setGhostTableWidth(table.scrollWidth);
        }
      } else {
        setGhostVisible(false);
      }
    };

    const observers: IntersectionObserver[] = [];
    orgCodes.forEach((orgCode) => {
      const thead = theadRefs.current.get(orgCode);
      const table = tableRefs.current.get(orgCode);
      if (!thead) return;
      const obs = new IntersectionObserver(() => updateGhost(), { threshold: 0 });
      obs.observe(thead);
      if (table) obs.observe(table);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [stickyHeader, groupedData]);

  // Sync horizontal scroll from content container to ghost header
  useEffect(() => {
    if (!stickyHeader || !scrollContainerRef?.current) return;
    const el = scrollContainerRef.current;
    const onScroll = () => {
      if (ghostScrollRef.current) {
        ghostScrollRef.current.scrollLeft = el.scrollLeft;
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [stickyHeader, scrollContainerRef]);

  // Sync scroll position when ghost first appears (content may already be scrolled)
  useEffect(() => {
    if (ghostVisible && ghostScrollRef.current && scrollContainerRef?.current) {
      ghostScrollRef.current.scrollLeft = scrollContainerRef.current.scrollLeft;
    }
  }, [ghostVisible, activeOrgCode, scrollContainerRef]);

  const getOrgName = (orgCode: string): string => {
    const chart = chartsOfAccounts?.find((c) => c.code === orgCode);
    return chart ? `${chart.name} (${orgCode})` : orgCode;
  };

  const renderHeaderCells = (row: AccountingLineDisplay, orgCode: string) => {
    const chartId = chartsOfAccounts?.find((chart) => chart.code === orgCode)?.id;
    return (
      <>
        {FILTERED_COLUMNS.map((key) => (
          <th key={key} className={cx({ th: true, header: true })}>
            <div className={styles.sortWrapper}>
              <span className={styles.headerLabel}>{intl.formatMessage({ id: `columns.${key}` })}</span>
            </div>
          </th>
        ))}
        {accounts[orgCode].map((account, index) => (
          <Fragment key={`header-${orgCode}-${account || `empty-${index}`}`}>
            <th className={cx({ th: true })} />
            <th className={cx({ th: true, taccountheader: account !== '', hidden: account === '' })} colSpan={2}>
              {chartId ? <AccountNameInfo chartId={chartId} accountCode={account} /> : <span>{account}</span>}
            </th>
          </Fragment>
        ))}
      </>
    );
  };

  const renderRowCells = (row: AccountingLineDisplay, orgCode: string) => {
    const acctCtx_s3 = row.acctCtx_s3;
    const acctDebit = row.acctDebit;
    const acctCredit = row.acctCredit;

    return (
      <>
        {FILTERED_COLUMNS.map((key) => (
          <td className={cx({ td: true })} key={key}>
            {row[key]}
          </td>
        ))}
        {accounts[orgCode].map((account, index) => {
          const isEmptyAccount = account === '';
          const isMatchingAccount = acctCtx_s3 === account;

          return (
            <Fragment key={`cell-${orgCode}-${account || `empty-${index}`}`}>
              <td className={cx({ td: true, separator: true })} />
              <td
                className={cx({
                  td: true,
                  taccountdebit: !isEmptyAccount,
                  hidden: isEmptyAccount,
                })}
              >
                {!isEmptyAccount && isMatchingAccount ? formatAmountCurrency(acctDebit) : ''}
              </td>
              <td
                className={cx({
                  td: true,
                  taccountcredit: !isEmptyAccount,
                  hidden: isEmptyAccount,
                })}
              >
                {!isEmptyAccount && isMatchingAccount ? formatAmountCurrency(acctCredit) : ''}
              </td>
            </Fragment>
          );
        })}
      </>
    );
  };

  const renderTotalRow = (orgCode: string) => {
    const totals = accountTotals[orgCode] || {};

    return (
      <>
        {FILTERED_COLUMNS.map((f) => (
          <td className={cx({ td: true })} key={f} />
        ))}
        {accounts[orgCode].map((account, index) => {
          const isEmptyAccount = account === '';
          const accountTotal = totals[account] || { debit: 0, credit: 0 };

          return (
            <Fragment key={`total-${orgCode}-${account || `empty-${index}`}`}>
              <td className={cx({ td: true, separator: true })} />
              <td
                className={cx({
                  td: true,
                  taccountdebit: !isEmptyAccount,
                  hidden: isEmptyAccount,
                })}
              >
                {!isEmptyAccount ? formatAmountCurrency(accountTotal.debit) : ''}
              </td>
              <td
                className={cx({
                  td: true,
                  taccountcredit: !isEmptyAccount,
                  hidden: isEmptyAccount,
                })}
              >
                {!isEmptyAccount ? formatAmountCurrency(accountTotal.credit) : ''}
              </td>
            </Fragment>
          );
        })}
      </>
    );
  };

  const ghostPortal =
    stickyHeader && ghostVisible && activeOrgCode && groupedData[activeOrgCode]
      ? createPortal(
          <div className={styles.ghostHeader} style={{ left: ghostLeft, width: ghostWidth }}>
            <div ref={ghostScrollRef} className={styles.ghostScroller}>
              <table
                className={cx({ table: true, taccounttable: true })}
                style={{ tableLayout: 'fixed', width: ghostTableWidth }}
              >
                <colgroup>
                  {ghostColWidths.map((w, i) => (
                    <col key={i} style={{ width: w }} />
                  ))}
                </colgroup>
                <thead className={cx({ thead: true })}>
                  <tr>{renderHeaderCells(groupedData[activeOrgCode][0], activeOrgCode)}</tr>
                </thead>
              </table>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div>
      {ghostPortal}
      {Object.entries(groupedData).map(([orgCode, orgData]) => (
        <table
          key={orgCode}
          ref={(el) => {
            if (el) tableRefs.current.set(orgCode, el);
            else tableRefs.current.delete(orgCode);
          }}
          className={cx({ table: true, taccounttable: true })}
        >
          <caption>
            <Heading4>{getOrgName(orgCode)}</Heading4>
          </caption>
          <thead
            ref={(el) => {
              if (el) theadRefs.current.set(orgCode, el);
              else theadRefs.current.delete(orgCode);
            }}
            className={cx({ thead: true })}
          >
            <tr>{renderHeaderCells(orgData[0], orgCode)}</tr>
          </thead>
          <tbody className={styles.tbody}>
            {orgData.map((row) => (
              <tr key={row._rowId}>{renderRowCells(row, orgCode)}</tr>
            ))}
            <tr className={cx({ sumRow: true })} key={`total-${orgCode}`}>
              {renderTotalRow(orgCode)}
            </tr>
          </tbody>
        </table>
      ))}
    </div>
  );
};
